// VX-4834 follow-up: reload-durable storage for the watchdog's consecutive-missed-pong sub-counter.
//
// VX-4834 made the ladder's OUTER counter (failedRecreateStore) survive a hard reload, but left every
// INPUT to it per-page. `missedPongCount` is a bare `let` inside the watchdog effect in
// useOpenPixelDataSharedWorker.ts, so it resets to 0 on every case-open. Reaching the recreate
// threshold takes two consecutive misses — one 30s ping interval plus a 10s pong timeout each, so
// ~70-80s of uninterrupted page life — while EXP-011's wedged worklist cases lived 7-60s. The count
// therefore never reached 2, the ladder never received even one increment, and persisting an outer
// counter that only ever holds zero changed nothing. EXP-011's first three runs showed exactly that:
// sustained wedges, zero organic bumps.
//
// Same lifetime and failure posture as failedRecreateStore: sessionStorage (per-tab, survives reload
// but not tab close), a generation-mismatch discard, a sliding TTL off the last write, and every
// error path answering "no misses observed" so a storage failure can only ever delay a recreate
// rather than force a spurious one.

// Keyed by TAB_ID alone — the same scope as failedRecreateStore, the counter this feeds.
//
// Deliberately NOT keyed by workerKey. workerKey is the caseSmid (ViewerContext.tsx:412), so it
// changes on every worklist case-open, which is precisely the event whose reload used to wipe the
// count. Including it would reset the count on every case-open and make this store a no-op for the
// only scenario it exists to fix. The evidence belongs to the tab's view of the shared worker
// lineage, not to a case: the SharedWorker outlives any one case, and a generation bump — not a
// case change — is what should void the count, which the `generation` field below handles.
//
// A tab that briefly holds two connections (the worklist's precache key alongside a viewer's case
// key) therefore pools their misses. That is intentional and matches failedRecreateStore: a missed
// pong on either connection is evidence about the same shared worker not answering this tab.
const storageKey = (tabId: string): string => `viewer.escapeHatch.missedPongs.${tabId}`;

// How long a recorded miss stays valid. Must comfortably exceed the gap between two consecutive
// misses across a reload — a miss lands ~40s into a connection (30s ping + 10s timeout), then the
// case-open navigation costs the page-load time, which EXP-011 measured at a p95 of ~68s — so the
// realistic worst-case gap is around two minutes. Four minutes clears that with margin while staying
// strictly under FAILED_RECREATE_EVIDENCE_TTL_MS (6 min), so the inner counter can never outlive the
// ladder evidence it feeds. Applied as a SLIDING window off the last write, not an absolute one off
// the first.
export const MISSED_PONG_EVIDENCE_TTL_MS = 4 * 60_000;

interface MissedPongEvidence {
  count: number;
  // Generation the misses were observed against; a bump starts a new lineage and voids them.
  generation: number;
  // Epoch ms of the most recent recorded miss — the TTL window's origin.
  updatedAt: number;
}

const parse = (raw: string): MissedPongEvidence | null => {
  const value = JSON.parse(raw) as Partial<MissedPongEvidence> | null;
  if (
    value == null ||
    typeof value.count !== 'number' ||
    typeof value.generation !== 'number' ||
    typeof value.updatedAt !== 'number'
  ) {
    return null;
  }
  return value as MissedPongEvidence;
};

export function readMissedPongs({
  tabId,
  generation,
  now,
}: {
  tabId: string;
  generation: number;
  now: number;
}): number {
  try {
    const raw = sessionStorage.getItem(storageKey(tabId));
    if (raw == null) return 0;
    const evidence = parse(raw);
    if (evidence == null) return 0;
    if (evidence.generation !== generation) return 0;
    // A negative age means the clock moved backwards since the write (NTP correction, VM resume, a
    // user changing the clock), leaving updatedAt in the future. Treating that as "fresh" would keep
    // the miss alive for the length of the skew on top of the TTL, so one stale miss could combine
    // with one unrelated later miss and recreate a healthy connection. An unverifiable timestamp is
    // not evidence: discard it, consistent with this ladder's rule that every default errs toward
    // NOT escalating.
    const ageMs = now - evidence.updatedAt;
    if (ageMs < 0 || ageMs > MISSED_PONG_EVIDENCE_TTL_MS) return 0;
    return evidence.count;
  } catch {
    // Malformed payload, or sessionStorage unavailable (privacy mode / disabled storage) — with no
    // trustworthy evidence the conservative answer is "no misses observed", which only ever delays a
    // recreate rather than triggering a spurious one.
    return 0;
  }
}

export function writeMissedPongs({
  tabId,
  generation,
  count,
  now,
}: {
  tabId: string;
  generation: number;
  count: number;
  now: number;
}): void {
  try {
    const evidence: MissedPongEvidence = { count, generation, updatedAt: now };
    sessionStorage.setItem(storageKey(tabId), JSON.stringify(evidence));
  } catch {
    // ignore write failures — the in-memory count still drives the watchdog within this page
  }
}

export function clearMissedPongs({ tabId }: { tabId: string }): void {
  try {
    sessionStorage.removeItem(storageKey(tabId));
  } catch {
    // ignore
  }
}
